// src/SallerAdmin/pages/Profile/BusinessProfilePage.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from '../../../contexts/UserContext';
import { Building, MapPin, Globe, Phone, Mail, FileText, Save, Loader2, ChevronDown } from 'lucide-react';

// --- Helper Components ---
const FormInput = ({ label, id, value, onChange, placeholder, required = false, type = "text", disabled = false }: any) => (
    <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
            type={type}
            id={id}
            name={id}
            value={value || ''}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-900 focus:border-blue-900 outline-none transition ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
        />
    </div>
);

const SectionHeader = ({ icon: Icon, title, isOpen, toggle }: any) => (
    <div 
        className="flex justify-between items-center bg-gray-50 px-4 py-3 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition"
        onClick={toggle}
    >
        <div className="flex items-center text-gray-800 font-bold text-lg">
            <Icon className="w-5 h-5 mr-2 text-blue-900" /> {title}
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
    </div>
);

const BusinessProfilePage: React.FC = () => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    
    // Section Toggles
    const [sections, setSections] = useState({ company: true, address: true, tax: true });
    
    // Form Data State
    const [formData, setFormData] = useState({
        companyName: '',
        companyEmail: '',
        officeContact: '',
        website: '',
        faxNumber: '',
        gstNumber: '',
        panNumber: '',
        aadhaarNumber: '',
        vatNumber: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'India'
    });

    // Helper to get Token
    const getToken = () => localStorage.getItem("token");

    // --- 1. Fetch Existing Data ---
    useEffect(() => {
        if (user?.id) {
            fetchBusinessProfile();
        }
    }, [user]);

    const fetchBusinessProfile = async () => {
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/business`, {
                headers: { 'Authorization': `Bearer ${getToken()}` }
            });
            if (res.ok) {
                const data = await res.json();
                if (data && data.companyName) {
                    setFormData(prev => ({ ...prev, ...data }));
                } else {
                    // Pre-fill from Basic User Info if Business Profile empty
                    setFormData(prev => ({
                        ...prev,
                        companyName: user?.companyName || '',
                        officeContact: user?.mobile || '',
                        companyEmail: user?.email || ''
                    }));
                }
            }
        } catch (e) { console.error("Fetch Error", e); }
        finally { setPageLoading(false); }
    };

    // --- 2. Handle Change ---
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // --- 3. Handle Submit ---
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            const res = await fetch(`/springboot/api/seller/profile/${user?.id}/business`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${getToken()}`
                },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                alert("Business Profile Saved Successfully in 'seller_business_profile'!");
                fetchBusinessProfile(); // Refresh
            } else {
                alert("Failed to save profile.");
            }
        } catch (e) {
            console.error(e);
            alert("Server Error");
        } finally {
            setLoading(false);
        }
    };

    // --- Auto-Fill Pincode Logic ---
    const handlePincodeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
        setFormData(prev => ({ ...prev, postalCode: val }));
        
        if (val.length === 6) {
            try {
                const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
                const data = await res.json();
                if (data[0].Status === "Success") {
                    const place = data[0].PostOffice[0];
                    setFormData(prev => ({ ...prev, city: place.District, state: place.State, country: 'India' }));
                }
            } catch (e) { console.error(e); }
        }
    };

    if (pageLoading) return <div className="p-10 text-center">Loading Business Profile...</div>;

    return (
        <div className="p-6 bg-gray-50 min-h-screen max-w-5xl mx-auto">
            <h1 className="text-3xl font-black text-gray-900 mb-8 border-b-4 border-blue-900 pb-2 inline-block">
                Business Profile & Financial Details
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">

                {/* --- SECTION 1: COMPANY DETAILS --- */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <SectionHeader icon={Building} title="Company Identity & Contact" isOpen={sections.company} toggle={() => setSections({ ...sections, company: !sections.company })} />
                    
                    {sections.company && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput id="companyName" label="Company Name" value={formData.companyName} onChange={handleChange} required placeholder="Registered Business Name" />
                            <FormInput id="website" label="Company Website" value={formData.website} onChange={handleChange} placeholder="https://www.example.com" />
                            
                            <FormInput id="companyEmail" label="Company Email" type="email" value={formData.companyEmail} onChange={handleChange} required placeholder="official@company.com" />
                            <FormInput id="officeContact" label="Office Contact Number" value={formData.officeContact} onChange={handleChange} required placeholder="+91 9999999999" />
                            
                            <FormInput id="faxNumber" label="FAX Number (Optional)" value={formData.faxNumber} onChange={handleChange} placeholder="Fax Number" />
                        </div>
                    )}
                </div>

                {/* --- SECTION 2: COMPANY ADDRESS --- */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <SectionHeader icon={MapPin} title="Registered Office Address" isOpen={sections.address} toggle={() => setSections({ ...sections, address: !sections.address })} />
                    
                    {sections.address && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <FormInput id="addressLine1" label="Address Line 1" value={formData.addressLine1} onChange={handleChange} required placeholder="Building, Street, Area" />
                                <FormInput id="addressLine2" label="Address Line 2" value={formData.addressLine2} onChange={handleChange} placeholder="Landmark, Floor, etc." />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Pincode *</label>
                                <input 
                                    type="text" 
                                    value={formData.postalCode} 
                                    onChange={handlePincodeChange}
                                    maxLength={6}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-900 outline-none"
                                    placeholder="Enter 6 digit pincode"
                                    required
                                />
                            </div>

                            <FormInput id="city" label="City" value={formData.city} onChange={handleChange} required placeholder="Auto-detected" />
                            <FormInput id="state" label="State" value={formData.state} onChange={handleChange} required placeholder="Auto-detected" />
                            <FormInput id="country" label="Country" value={formData.country} onChange={handleChange} disabled />
                        </div>
                    )}
                </div>

                {/* --- SECTION 3: REGISTRATION & TAXATION --- */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <SectionHeader icon={FileText} title="Registration & Taxation (KYC)" isOpen={sections.tax} toggle={() => setSections({ ...sections, tax: !sections.tax })} />
                    
                    {sections.tax && (
                        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <FormInput id="gstNumber" label="GST Number" value={formData.gstNumber} onChange={handleChange} required placeholder="GSTIN (15 Digits)" />
                            <FormInput id="panNumber" label="Company PAN" value={formData.panNumber} onChange={handleChange} required placeholder="Permanent Account Number" />
                            
                            <FormInput id="aadhaarNumber" label="Owner's Aadhaar Number" value={formData.aadhaarNumber} onChange={handleChange} required placeholder="12 Digit Aadhaar" />
                            <FormInput id="vatNumber" label="VAT Number (Optional)" value={formData.vatNumber} onChange={handleChange} placeholder="If applicable" />
                        </div>
                    )}
                </div>

                {/* --- SUBMIT BUTTON --- */}
                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center justify-center px-8 py-3 bg-red-600 text-white font-bold rounded-lg shadow-md hover:bg-red-700 transition disabled:opacity-70"
                    >
                        {loading ? <Loader2 className="animate-spin mr-2" /> : <Save className="mr-2" />}
                        {loading ? 'Saving to Database...' : 'Save Business Profile'}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default BusinessProfilePage;